import { html } from 'lit';
import { msg } from '@lit/localize';
import { errorClassName } from '../styles/form-control.styles.js';
const origCheckValidity = new WeakMap();
const origReportValidity = new WeakMap();
const elementsRegistry = new WeakMap();
const activeMappingClassName = 'active-mapping';
/**
 * This reactive controller used for constraint validation API implementation.
 *
 * **Usage**
 * 1. Extend classMap a top-level element in your control with {@link ValidationController.classMap}
 *    ```ts
 *     classMap({...this.validationController.classMap})
 *    ```
 * 2. Render {@link ValidationController.renderErrorSlot} in component's `render`
 *    ```ts
 *     ${this.validationController.renderErrorSlot()}
 *    ```
 * 3. Delegate `setCustomValidity` method to {@link ValidationController.setCustomValidity}
 *     ```ts
 *     setCustomValidity(message: string){
 *         this.validationController.setCustomValidity(message)
 *     }
 *    ```
 * 4. When you need to set an error text, set {@link ValidationController.errorText} to a desired
 *    value
 *    ```ts
 *     this.validationController.errorText = 'Some error'
 *    ```
 * 5. When you want to show an error, e.g. in `reportValidity` method, set
 *    {@link ValidationController.showError} to `true`, and to `false` to hide it.
 *    ```ts
 *     this.validationController.showError = true
 *    ```
 * 6. To determine if a component has a custom validity (which can be set via `setCustomValidity`
 *    method of your component directly, or by `error-text` attribute, or by `error-text` slot),
 *    i.e. in your component `validity` getter check {@link ValidationController.hasCustomValidity}
 *    field.
 *    ```ts
 *     if ( this.validationController.hasCustomValidity ) {
 *        return createValidity('customError')
 *     }
 *    ```
 * 7. Implement `focus` method to actually set focus onto something inside your component.
 */
export class ValidationController {
    /** Error text that is used in visible error message, i.e. it is updated only after `reportValidity()` */
    get errorText() {
        if (this.host.errorText) {
            return this.host.errorText;
        }
        return this._errorText;
    }
    set errorText(errorText) {
        this._errorText = errorText;
        this.nextErrorText = null;
        this.host.requestUpdate();
    }
    /** Error text that is used in `validationMessage`, i.e. it is updated immediately after `setCustomValidity` */
    get validationMessage() {
        this.checkValidityMapping();
        if (this.hasActiveErrorMapping) {
            const activeMapping = this.getErrorMappings().find((m) => m.classList.contains(activeMappingClassName));
            if (activeMapping) {
                return activeMapping.textContent;
            }
        }
        if (this.nextErrorText !== null) {
            return this.nextErrorText;
        }
        return this.errorText;
    }
    get hasCustomValidity() {
        return this._hasCustomValidity || Boolean(this.host.errorText?.trim());
    }
    get showError() {
        return this._showError;
    }
    set showError(showError) {
        this._showError = showError;
        this.host.requestUpdate();
    }
    constructor(host) {
        // state
        this.nextErrorText = null;
        this.hasActiveErrorMapping = false;
        this._errorText = '';
        this._hasCustomValidity = false;
        this._showError = false;
        this.setByAttribute = false;
        this.handleFormSubmit = (event) => {
            const shouldValidate = !this.form?.hasAttribute('novalidate');
            if (shouldValidate && !this.form?.reportValidity()) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        };
        this.checkValidity = () => {
            let valid = true;
            elementsRegistry.get(this.form).forEach((control) => {
                if (!control.disabled && !control.checkValidity()) {
                    valid = false;
                }
            });
            return valid;
        };
        this.reportValidity = () => {
            const invalids = Array.from(elementsRegistry.get(this.form)).filter((control) => !control.disabled && !control.reportValidity());
            if (invalids.length > 0) {
                invalids.sort((el1, el2) => getY(el1) - getY(el2))[0].focus();
                return false;
            }
            return true;
        };
        this.setBySlot = false;
        this.handleErrorSlotChange = (e) => {
            // check that error is actually inserted in slot from the light DOM because
            // FireFox & Safari trigger slotchange on changes from withing the component
            if (this.hasErrorSlot()) {
                const slotted = e.target.assignedNodes({ flatten: true });
                const text = slotted
                    .map((el) => {
                    return el.textContent || '';
                })
                    .join('');
                this._hasCustomValidity = Boolean(text.trim());
                this._errorText = text.trim();
                this.setBySlot = true;
            }
            else if (this.setBySlot) {
                this._hasCustomValidity = false;
                this.errorText = '';
            }
        };
        this.host = host;
        this.host.addController(this);
    }
    /** used by `ReactiveControllerHost` */
    hostConnected() {
        const form = this.host.getForm();
        if (form) {
            this.attachForm(form);
        }
    }
    /** used by `ReactiveControllerHost` */
    hostDisconnected() {
        this.detachForm();
    }
    /** used by `ReactiveControllerHost` */
    hostUpdate() {
        const form = this.host.getForm();
        if (!form) {
            this.detachForm();
        }
        if (form && this.form !== form) {
            this.detachForm();
            this.attachForm(form);
        }
        if (this.host.showError !== this.showError) {
            if (this.host.showError) {
                this.checkValidityMapping();
                this._showError = true;
                this.setByAttribute = true;
            }
            else if (this.setByAttribute) {
                this._showError = false;
            }
        }
    }
    hostUpdated() { }
    setCustomValidity(text) {
        this.nextErrorText = text;
        this._hasCustomValidity = Boolean(text.trim());
    }
    updateCustomValidity() {
        if (this.hasErrorSlot()) {
            return;
        }
        this._errorText = this.nextErrorText || '';
        this.nextErrorText = null;
        if (!Boolean(this.errorText.trim())) {
            this._showError = false;
        }
        this.host.requestUpdate();
    }
    get classMap() {
        return {
            'hlx-form-control': true,
            [errorClassName.cssText]: this.showError,
            'hlx-form-control-has-active-mapping': this.hasActiveErrorMapping,
        };
    }
    renderErrorSlot(customClass) {
        let className = 'hlx-form-control-error';
        if (customClass) {
            className = `${className} ${customClass}`;
        }
        return html `
            <div part="hlx-form-control-error" class=${className}>
                <slot
                    name="error-icon"
                    class="hlx-form-control-error-icon"
                    part="hlx-form-control-error-icon">
                    <hlx-icon name="circle-exclamation" title="${msg('Error icon')}"></hlx-icon>
                </slot>
                <slot
                    name="error-mapping"
                    class="error-mapping"
                    part="hlx-form-control-error-mapping"></slot>
                <slot
                    name="error-text"
                    part="hlx-form-control-error-text"
                    role="alert"
                    aria-live="assertive"
                    class="error-text"
                    @slotchange=${this.handleErrorSlotChange}>
                    ${this.errorText}
                </slot>
            </div>
        `;
    }
    checkValidityMapping() {
        const validity = this.host.validity;
        const mappings = this.getErrorMappings();
        this.hasActiveErrorMapping = false;
        for (const el of mappings) {
            el.classList.remove(activeMappingClassName);
        }
        for (const el of mappings) {
            if (validity[el.type]) {
                el.classList.add(activeMappingClassName);
                this.hasActiveErrorMapping = true;
                break;
            }
        }
    }
    getErrorMappings() {
        const slot = this.host.shadowRoot.querySelector('slot[name="error-mapping"]');
        return getAllMappingsInside(slot);
    }
    attachForm(form) {
        if (form) {
            this.form = form;
            // for a new form:
            if (!elementsRegistry.has(this.form)) {
                // create form's elements registry,
                elementsRegistry.set(this.form, new Set());
                // set submit handler,
                this.form.addEventListener('submit', this.handleFormSubmit);
                // and replace validation methods, saving originals,
                origCheckValidity.set(this.form, this.form.checkValidity);
                this.form.checkValidity = this.checkValidity;
                origReportValidity.set(this.form, this.form.reportValidity);
                this.form.reportValidity = this.reportValidity;
            }
            // add current form control to registry
            elementsRegistry.get(this.form).add(this.host);
        }
        else {
            this.form = undefined;
        }
    }
    detachForm() {
        if (this.form) {
            // drop current element from the elements registry
            elementsRegistry.get(this.form).delete(this.host);
            // when elements registry for a form is empty:
            if (elementsRegistry.get(this.form).size === 0) {
                // delete the registry,
                elementsRegistry.delete(this.form);
                // remove submit listener,
                this.form.removeEventListener('submit', this.handleFormSubmit);
                // restore original validation methods,
                this.form.checkValidity = origCheckValidity.get(this.form);
                this.form.reportValidity = origReportValidity.get(this.form);
                // remove stored original validation methods.
                origCheckValidity.delete(this.form);
                origReportValidity.delete(this.form);
            }
        }
        this.form = undefined;
    }
    hasErrorSlot() {
        return this.host.querySelector(`:scope > [slot="error-text"]`);
    }
}
function getY(el) {
    return el.getBoundingClientRect().top;
}
function getAllMappingsInside(slot) {
    const mappings = [];
    slot?.assignedElements().forEach((el) => {
        if (el.tagName === 'HLX-ERROR-MAPPING') {
            mappings.push(el);
        }
        else if (el instanceof HTMLSlotElement) {
            mappings.push(...getAllMappingsInside(el));
        }
        else {
            el.querySelectorAll('hlx-error-mapping').forEach((el) => {
                mappings.push(el);
            });
        }
    });
    return mappings;
}
//# sourceMappingURL=validation.controller.js.map